import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ExcelJS from 'exceljs';
import continuousServiceService from '../../services/continuousServiceService';
import { formatDateLocaleLima, toDateOnlyIsoKey } from '../../utils/dateUtils';

const PrintContinuousService = () => {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const printContentRef = useRef(null);
  const [printScale, setPrintScale] = useState(1);

  useEffect(() => {
    loadService();
  }, [id]);

  // Calcular escala para que todo quepa en una hoja (tabla + observaciones + firmas)
  useEffect(() => {
    if (!service || !printContentRef.current) return;
    const timer = setTimeout(() => {
      const el = printContentRef.current;
      if (!el) return;
      const isLandscape = !!service.include_areas && (service.progress || []).length > 0;
      const pageHeightMm = isLandscape ? 200 : 287;
      const firmasReserveMm = 24;
      const pageHeightPx = pageHeightMm * 3.78;
      const contentHeight = el.scrollHeight;
      const safetyFactor = 0.88;
      const usableHeight = (pageHeightMm - firmasReserveMm) * 3.78 * safetyFactor;
      const heightScaleFactor = 0.72;
      const scaleYNeeded = usableHeight / contentHeight;
      const scale = contentHeight <= usableHeight ? 1 : scaleYNeeded / heightScaleFactor;
      setPrintScale(Math.min(1, Math.max(0.2, scale)));
    }, 250);
    return () => clearTimeout(timer);
  }, [service]);

  const loadService = async () => {
    try {
      const response = await continuousServiceService.getContinuousService(id);
      if (response.success) {
        setService(response.data);
      }
    } catch (error) {
      console.error('Error al cargar servicio continuo:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando servicio continuo...</p>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 text-lg">Servicio continuo no encontrado</p>
        </div>
      </div>
    );
  }

  // Formatear fechas
  const startDate = formatDateLocaleLima(service.start_date);
  const endDate = service.end_date ? formatDateLocaleLima(service.end_date) : 'En curso';

  // Calcular totales desde progress (igual que en la presentación): base por grupo, IGV = base × tasa
  const calculateServiceTotals = () => {
    const progress = service.progress || [];
    let baseImponible18 = 0;
    let baseImponible10 = 0;
    let baseImponible10_5 = 0;
    let noGravados = 0;
    progress.forEach((item) => {
      const subtotal = parseFloat(item.subtotal || 0);
      const taxRate = parseFloat(item.tax_rate || 0);
      if (Math.abs(taxRate - 18) < 0.01) baseImponible18 += subtotal;
      else if (Math.abs(taxRate - 10) < 0.01) baseImponible10 += subtotal;
      else if (Math.abs(taxRate - 10.5) < 0.01) baseImponible10_5 += subtotal;
      else noGravados += subtotal;
    });
    const igv18 = Math.round(baseImponible18 * 0.18 * 100) / 100;
    const igv10 = Math.round(baseImponible10 * 0.1 * 100) / 100;
    const igv10_5 = Math.round(baseImponible10_5 * 0.105 * 100) / 100;
    const total = baseImponible18 + igv18 + baseImponible10 + igv10 + baseImponible10_5 + igv10_5 + noGravados;
    return { baseImponible18, baseImponible10, baseImponible10_5, noGravados, igv18, igv10, igv10_5, total };
  };

  const serviceTotals = service.progress && service.progress.length > 0 ? calculateServiceTotals() : {
    baseImponible18: parseFloat(service.base_imponible || 0),
    baseImponible10: 0,
    baseImponible10_5: 0,
    noGravados: parseFloat(service.no_gravados || 0),
    igv18: parseFloat(service.igv_amount || 0),
    igv10: 0,
    igv10_5: 0,
    total: parseFloat(service.total_amount || 0)
  };

  const formatCurrency = (num) => parseFloat(num || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Convertir número entero a letras en español (para "Son:")
  const intToSpanishWords = (n) => {
    if (n === 0) return 'CERO';
    const unidades = ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
    const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
    const decenas = ['', '', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
    const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];
    const toWords = (num) => {
      if (num === 0) return '';
      if (num === 100) return 'CIEN';
      if (num < 10) return unidades[num];
      if (num < 20) return especiales[num - 10];
      if (num < 100) {
        const d = Math.floor(num / 10);
        const u = num % 10;
        if (u === 0) return decenas[d];
        if (d === 2) return 'VEINTI' + unidades[u];
        return decenas[d] + ' Y ' + unidades[u];
      }
      if (num < 1000) {
        const c = Math.floor(num / 100);
        const rest = num % 100;
        return (centenas[c] + (rest ? ' ' + toWords(rest) : '')).trim();
      }
      if (num < 1000000) {
        const mil = Math.floor(num / 1000);
        const rest = num % 1000;
        const milStr = mil === 1 ? 'MIL' : toWords(mil) + ' MIL';
        return (milStr + (rest ? ' ' + toWords(rest) : '')).trim();
      }
      const mill = Math.floor(num / 1000000);
      const rest = num % 1000000;
      const millStr = mill === 1 ? 'UN MILLÓN' : toWords(mill) + ' MILLONES';
      return (millStr + (rest ? ' ' + toWords(rest) : '')).trim();
    };
    return toWords(n);
  };

  // Convertir total a letras (entero en palabras + decimales)
  const numberToWords = (num) => {
    const entero = Math.floor(num);
    const decimal = Math.round((num - entero) * 100);
    const parteEntera = intToSpanishWords(entero);
    return `${parteEntera} CON ${decimal}/100 SOLES`;
  };
  const totalInWords = numberToWords(serviceTotals.total);

  const ROWS_PER_PAGE_PIVOT = 17;  // (solo referencia; impresión en una sola hoja)
  const ROWS_PER_PAGE_DETAIL = 35;  // (solo referencia; impresión en una sola hoja)
  const chunk = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  // Ordenar registros de avance del más antiguo al más nuevo (igual que en detalle)
  const sortedProgress = (service.progress || []).slice().sort((a, b) => {
    const dateA = new Date(a.progress_date || 0);
    const dateB = new Date(b.progress_date || 0);
    return dateA - dateB;
  });

  // Sin áreas: agrupar por producto (servicio) y aplanar para mostrar agrupado (mismo orden: producto, luego fecha)
  const groupedByService = (() => {
    const map = new Map();
    sortedProgress.forEach((item) => {
      const key = item.service?.id ?? 'sin';
      if (!map.has(key)) {
        map.set(key, { service: item.service, items: [] });
      }
      map.get(key).items.push(item);
    });
    return Array.from(map.values())
      .sort((a, b) => (a.service?.name || '').localeCompare(b.service?.name || ''));
  })();
  const sortedProgressByProduct = groupedByService.flatMap((g) => g.items);

  // Bloques para impresión: un solo bloque por servicio con todas las filas (una sola hoja)
  const detailBlocksForPrint = (() => {
    const blocks = [];
    groupedByService.forEach((g) => {
      blocks.push({
        serviceName: g.service?.name || 'Sin servicio',
        serviceSku: g.service?.sku || null,
        items: g.items,
        isContinuation: false,
      });
    });
    return blocks;
  })();

  const statusLabel = service.status === 'open' ? 'Abierto' : 'Cerrado';
  const statusColor = service.status === 'open' ? '#10b981' : '#6b7280';
  const includeAreas = !!service.include_areas;

  // Pivot por área para un subconjunto de progress: columnas = áreas, filas = días
  const buildPivotForProgress = (progressItems) => {
    if (!progressItems.length) return { areasList: [], dates: [], grid: {}, columnTotals: {}, grandTotal: 0 };

    const dateStr = (d) => toDateOnlyIsoKey(d);
    const areaKey = (item) => (item.area && item.area.id ? `area_${item.area.id}` : 'sin_area');

    const areasSet = new Map();
    const datesSet = new Set();
    const grid = {};

    progressItems.forEach((item) => {
      const d = dateStr(item.progress_date);
      datesSet.add(d);
      const key = areaKey(item);
      if (key === 'sin_area') {
        if (!areasSet.has(key)) areasSet.set(key, { id: null, name: 'Sin área', code: null });
      } else if (item.area) {
        if (!areasSet.has(key)) areasSet.set(key, { id: item.area.id, name: item.area.name, code: item.area.code });
      }
      if (!grid[d]) grid[d] = {};
      grid[d][key] = (grid[d][key] || 0) + parseFloat(item.quantity || 0);
    });

    const areasList = Array.from(areasSet.entries())
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const dates = Array.from(datesSet).sort();

    const columnTotals = {};
    areasList.forEach(({ key }) => { columnTotals[key] = 0; });
    let grandTotal = 0;
    dates.forEach((d) => {
      areasList.forEach(({ key }) => {
        const val = (grid[d] && grid[d][key]) || 0;
        columnTotals[key] = (columnTotals[key] || 0) + val;
        grandTotal += val;
      });
    });

    return { areasList, dates, grid, columnTotals, grandTotal };
  };

  // Agrupar por servicio y construir un pivote por cada uno
  const pivotsByService = (() => {
    const progress = service.progress || [];
    if (!includeAreas || progress.length === 0) return [];

    const byService = new Map(); // serviceKey -> { serviceName, serviceSku, items[] }
    progress.forEach((item) => {
      const sk = item.service && item.service.id != null ? `s_${item.service.id}` : 'sin_servicio';
      if (!byService.has(sk)) {
        byService.set(sk, {
          serviceKey: sk,
          serviceName: item.service ? item.service.name : 'Sin servicio',
          serviceSku: item.service && item.service.sku ? item.service.sku : null,
          items: []
        });
      }
      byService.get(sk).items.push(item);
    });

    return Array.from(byService.values())
      .sort((a, b) => (a.serviceName || '').localeCompare(b.serviceName || ''))
      .map(({ serviceKey, serviceName, serviceSku, items }) => ({
        serviceKey,
        serviceName,
        serviceSku,
        ...buildPivotForProgress(items)
      }))
      .filter((p) => p.dates.length > 0);
  })();

  const useLandscape = pivotsByService.length > 0;
  const maxPivotColumns = pivotsByService.length > 0
    ? Math.max(...pivotsByService.map((p) => p.areasList.length))
    : 0;
  const manyColumns = maxPivotColumns > 6;

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Sistema Logístico';
      const sheet = workbook.addWorksheet('Servicio Continuo', { views: [{ state: 'frozen', ySplit: 4 }] });

      const teal = 'FF0d9488';
      const tealLight = 'FFE6F7F5';
      const grayLight = 'FFF3F4F6';
      const yellowTotal = 'FFFEF3C7';

      const borderThin = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      const borderMedium = {
        top: { style: 'medium' },
        left: { style: 'medium' },
        bottom: { style: 'medium' },
        right: { style: 'medium' }
      };

      let row = 1;
      sheet.mergeCells(row, 1, row, 8);
      const titleCell = sheet.getCell(row, 1);
      titleCell.value = 'SERVICIO CONTINUO';
      titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: teal } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.border = borderThin;
      row += 2;

      sheet.getCell(row, 1).value = 'Servicio:';
      sheet.getCell(row, 1).font = { bold: true, color: { argb: teal } };
      sheet.getCell(row, 2).value = service.code;
      sheet.getCell(row, 4).value = 'Inicio:';
      sheet.getCell(row, 4).font = { bold: true, color: { argb: teal } };
      sheet.getCell(row, 5).value = startDate;
      sheet.getCell(row, 6).value = 'Fin:';
      sheet.getCell(row, 6).font = { bold: true, color: { argb: teal } };
      sheet.getCell(row, 7).value = endDate;
      sheet.getCell(row, 8).value = 'Estado:';
      sheet.getCell(row, 8).font = { bold: true, color: { argb: teal } };
      sheet.getCell(row, 9).value = service.status === 'open' ? 'Abierto' : 'Cerrado';
      row++;
      sheet.getCell(row, 1).value = 'Proveedor:';
      sheet.getCell(row, 1).font = { bold: true, color: { argb: teal } };
      sheet.getCell(row, 2).value = service.supplier?.name || 'N/A';
      if (service.supplier?.document_number) {
        sheet.getCell(row, 4).value = 'RUC:';
        sheet.getCell(row, 4).font = { bold: true, color: { argb: teal } };
        sheet.getCell(row, 5).value = service.supplier.document_number;
      }
      row += 2;

      if (pivotsByService.length > 0) {
        pivotsByService.forEach((pivot) => {
          const title = pivot.serviceSku
            ? `REGISTROS DE AVANCE - ${pivot.serviceName} (${pivot.serviceSku})`
            : `REGISTROS DE AVANCE - ${pivot.serviceName}`;
          sheet.mergeCells(row, 1, row, pivot.areasList.length + 2);
          const titleCellPivot = sheet.getCell(row, 1);
          titleCellPivot.value = title;
          titleCellPivot.font = { bold: true, size: 12, color: { argb: teal } };
          titleCellPivot.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
          titleCellPivot.border = borderThin;
          titleCellPivot.alignment = { horizontal: 'left', vertical: 'middle' };
          row++;
          const headerRow = row;
          sheet.getCell(row, 1).value = 'Fecha';
          sheet.getCell(row, 1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
          sheet.getCell(row, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: teal } };
          sheet.getCell(row, 1).border = borderThin;
          sheet.getCell(row, 1).alignment = { horizontal: 'center', vertical: 'middle' };
          pivot.areasList.forEach((a, c) => {
            const col = 2 + c;
            sheet.getCell(row, col).value = a.name || a.code || '-';
            sheet.getCell(row, col).font = { bold: true, color: { argb: 'FFFFFFFF' } };
            sheet.getCell(row, col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: teal } };
            sheet.getCell(row, col).border = borderThin;
            sheet.getCell(row, col).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
          });
          const totalCol = 2 + pivot.areasList.length;
          sheet.getCell(row, totalCol).value = 'TOTAL';
          sheet.getCell(row, totalCol).font = { bold: true, color: { argb: 'FFFFFFFF' } };
          sheet.getCell(row, totalCol).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: teal } };
          sheet.getCell(row, totalCol).border = borderThin;
          sheet.getCell(row, totalCol).alignment = { horizontal: 'center', vertical: 'middle' };
          row++;
          pivot.dates.forEach((d, idx) => {
            let rowTotal = 0;
            sheet.getCell(row, 1).value = formatDateLocaleLima(d);
            sheet.getCell(row, 1).border = borderThin;
            sheet.getCell(row, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: idx % 2 === 0 ? 'FFFFFFFF' : grayLight } };
            pivot.areasList.forEach((a, c) => {
              const val = (pivot.grid[d] && pivot.grid[d][a.key]) || 0;
              rowTotal += val;
              const cell = sheet.getCell(row, 2 + c);
              cell.value = val === 0 ? '' : Number(val);
              cell.border = borderThin;
              cell.alignment = { horizontal: 'right', vertical: 'middle' };
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: idx % 2 === 0 ? 'FFFFFFFF' : grayLight } };
            });
            sheet.getCell(row, totalCol).value = rowTotal;
            sheet.getCell(row, totalCol).border = borderThin;
            sheet.getCell(row, totalCol).alignment = { horizontal: 'right', vertical: 'middle' };
            sheet.getCell(row, totalCol).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: idx % 2 === 0 ? 'FFFFFFFF' : grayLight } };
            row++;
          });
          sheet.getCell(row, 1).value = 'TOTAL';
          sheet.getCell(row, 1).font = { bold: true };
          sheet.getCell(row, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: yellowTotal } };
          sheet.getCell(row, 1).border = borderThin;
          pivot.areasList.forEach((a, c) => {
            const cell = sheet.getCell(row, 2 + c);
            cell.value = pivot.columnTotals[a.key] === 0 ? '' : Number(pivot.columnTotals[a.key]);
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: yellowTotal } };
            cell.border = borderThin;
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          });
          sheet.getCell(row, totalCol).value = pivot.grandTotal;
          sheet.getCell(row, totalCol).font = { bold: true };
          sheet.getCell(row, totalCol).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: yellowTotal } };
          sheet.getCell(row, totalCol).border = borderThin;
          sheet.getCell(row, totalCol).alignment = { horizontal: 'right', vertical: 'middle' };
          row += 2;
        });
      } else if (groupedByService.length > 0) {
        groupedByService.forEach((g) => {
          const title = g.service?.sku ? `${g.service?.name || 'Sin servicio'} (${g.service.sku})` : (g.service?.name || 'Sin servicio');
          sheet.mergeCells(row, 1, row, 6);
          const titleCellG = sheet.getCell(row, 1);
          titleCellG.value = title;
          titleCellG.font = { bold: true, size: 12, color: { argb: teal } };
          titleCellG.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
          titleCellG.border = borderThin;
          row++;
          sheet.getCell(row, 1).value = 'Fecha';
          sheet.getCell(row, 2).value = 'Proyecto';
          sheet.getCell(row, 3).value = 'Descripción';
          sheet.getCell(row, 4).value = 'Cant.';
          sheet.getCell(row, 5).value = 'P.Unit';
          sheet.getCell(row, 6).value = 'Subtotal';
          for (let c = 1; c <= 6; c++) {
            sheet.getCell(row, c).font = { bold: true, color: { argb: 'FFFFFFFF' } };
            sheet.getCell(row, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: teal } };
            sheet.getCell(row, c).border = borderThin;
            sheet.getCell(row, c).alignment = { horizontal: c >= 4 ? 'right' : 'left', vertical: 'middle' };
          }
          row++;
          g.items.forEach((item, idx) => {
            sheet.getCell(row, 1).value = formatDateLocaleLima(item.progress_date);
            sheet.getCell(row, 2).value = item.project ? `${item.project.code} ${item.project.name}` : '-';
            sheet.getCell(row, 3).value = item.description || '-';
            sheet.getCell(row, 4).value = Number(item.quantity || 0);
            sheet.getCell(row, 4).numFmt = '#,##0.00';
            sheet.getCell(row, 5).value = Number(item.unit_price || 0);
            sheet.getCell(row, 5).numFmt = '"S/ "#,##0.00';
            sheet.getCell(row, 6).value = Number(item.subtotal || 0);
            sheet.getCell(row, 6).numFmt = '"S/ "#,##0.00';
            const bg = idx % 2 === 0 ? 'FFFFFFFF' : grayLight;
            for (let c = 1; c <= 6; c++) {
              sheet.getCell(row, c).border = borderThin;
              sheet.getCell(row, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
              sheet.getCell(row, c).alignment = { horizontal: c >= 4 ? 'right' : 'left', vertical: 'middle' };
            }
            row++;
          });
          row += 2;
        });
      }

      row++;
      sheet.getCell(row, 1).value = 'Observaciones:';
      sheet.getCell(row, 1).font = { bold: true, color: { argb: teal } };
      sheet.mergeCells(row, 2, row, 6);
      sheet.getCell(row, 2).value = service.notes || '';
      sheet.getCell(row, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
      sheet.getCell(row, 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
      sheet.getCell(row, 1).border = { ...borderThin, top: { style: 'medium' } };
      sheet.getCell(row, 2).border = { ...borderThin, top: { style: 'medium' } };
      sheet.getCell(row, 2).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      row += 2;

      if (serviceTotals.baseImponible18 > 0) {
        sheet.getCell(row, 1).value = 'Base 18%:';
        sheet.getCell(row, 1).font = { bold: true, color: { argb: teal } };
        sheet.getCell(row, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 1).border = borderThin;
        sheet.getCell(row, 2).value = Number(serviceTotals.baseImponible18);
        sheet.getCell(row, 2).numFmt = '"S/ "#,##0.00';
        sheet.getCell(row, 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 2).border = borderThin;
        sheet.getCell(row, 2).alignment = { horizontal: 'right', vertical: 'middle' };
        sheet.getCell(row, 3).value = 'IGV:';
        sheet.getCell(row, 3).font = { bold: true, color: { argb: teal } };
        sheet.getCell(row, 3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 3).border = borderThin;
        sheet.getCell(row, 4).value = Number(serviceTotals.igv18);
        sheet.getCell(row, 4).numFmt = '"S/ "#,##0.00';
        sheet.getCell(row, 4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 4).border = borderThin;
        sheet.getCell(row, 4).alignment = { horizontal: 'right', vertical: 'middle' };
        const isLastBase = serviceTotals.baseImponible10 === 0 && serviceTotals.baseImponible10_5 === 0;
        if (isLastBase) {
          sheet.getCell(row, 5).value = 'Total:';
          sheet.getCell(row, 5).font = { bold: true, color: { argb: teal } };
          sheet.getCell(row, 5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
          sheet.getCell(row, 5).border = borderThin;
          sheet.getCell(row, 6).value = Number(serviceTotals.total);
          sheet.getCell(row, 6).numFmt = '"S/ "#,##0.00';
          sheet.getCell(row, 6).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
          sheet.getCell(row, 6).border = borderThin;
          sheet.getCell(row, 6).alignment = { horizontal: 'right', vertical: 'middle' };
        }
        row++;
      }
      if (serviceTotals.baseImponible10 > 0) {
        sheet.getCell(row, 1).value = 'Base 10%:';
        sheet.getCell(row, 1).font = { bold: true, color: { argb: teal } };
        sheet.getCell(row, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 1).border = borderThin;
        sheet.getCell(row, 2).value = Number(serviceTotals.baseImponible10);
        sheet.getCell(row, 2).numFmt = '"S/ "#,##0.00';
        sheet.getCell(row, 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 2).border = borderThin;
        sheet.getCell(row, 2).alignment = { horizontal: 'right', vertical: 'middle' };
        sheet.getCell(row, 3).value = 'IGV:';
        sheet.getCell(row, 3).font = { bold: true, color: { argb: teal } };
        sheet.getCell(row, 3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 3).border = borderThin;
        sheet.getCell(row, 4).value = Number(serviceTotals.igv10);
        sheet.getCell(row, 4).numFmt = '"S/ "#,##0.00';
        sheet.getCell(row, 4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 4).border = borderThin;
        sheet.getCell(row, 4).alignment = { horizontal: 'right', vertical: 'middle' };
        const isLastBase = serviceTotals.baseImponible10_5 === 0;
        if (isLastBase) {
          sheet.getCell(row, 5).value = 'Total:';
          sheet.getCell(row, 5).font = { bold: true, color: { argb: teal } };
          sheet.getCell(row, 5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
          sheet.getCell(row, 5).border = borderThin;
          sheet.getCell(row, 6).value = Number(serviceTotals.total);
          sheet.getCell(row, 6).numFmt = '"S/ "#,##0.00';
          sheet.getCell(row, 6).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
          sheet.getCell(row, 6).border = borderThin;
          sheet.getCell(row, 6).alignment = { horizontal: 'right', vertical: 'middle' };
        }
        row++;
      }
      if (serviceTotals.baseImponible10_5 > 0) {
        sheet.getCell(row, 1).value = 'Base 10.5%:';
        sheet.getCell(row, 1).font = { bold: true, color: { argb: teal } };
        sheet.getCell(row, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 1).border = borderThin;
        sheet.getCell(row, 2).value = Number(serviceTotals.baseImponible10_5);
        sheet.getCell(row, 2).numFmt = '"S/ "#,##0.00';
        sheet.getCell(row, 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 2).border = borderThin;
        sheet.getCell(row, 2).alignment = { horizontal: 'right', vertical: 'middle' };
        sheet.getCell(row, 3).value = 'IGV:';
        sheet.getCell(row, 3).font = { bold: true, color: { argb: teal } };
        sheet.getCell(row, 3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 3).border = borderThin;
        sheet.getCell(row, 4).value = Number(serviceTotals.igv10_5);
        sheet.getCell(row, 4).numFmt = '"S/ "#,##0.00';
        sheet.getCell(row, 4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 4).border = borderThin;
        sheet.getCell(row, 4).alignment = { horizontal: 'right', vertical: 'middle' };
        sheet.getCell(row, 5).value = 'Total:';
        sheet.getCell(row, 5).font = { bold: true, color: { argb: teal } };
        sheet.getCell(row, 5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 5).border = borderThin;
        sheet.getCell(row, 6).value = Number(serviceTotals.total);
        sheet.getCell(row, 6).numFmt = '"S/ "#,##0.00';
        sheet.getCell(row, 6).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 6).border = borderThin;
        sheet.getCell(row, 6).alignment = { horizontal: 'right', vertical: 'middle' };
        row++;
      }
      if (serviceTotals.baseImponible18 === 0 && serviceTotals.baseImponible10 === 0 && serviceTotals.baseImponible10_5 === 0) {
        sheet.getCell(row, 1).value = 'Total:';
        sheet.getCell(row, 1).font = { bold: true, color: { argb: teal } };
        sheet.getCell(row, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 1).border = borderThin;
        sheet.getCell(row, 2).value = Number(serviceTotals.total);
        sheet.getCell(row, 2).numFmt = '"S/ "#,##0.00';
        sheet.getCell(row, 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 2).border = borderThin;
        sheet.getCell(row, 2).alignment = { horizontal: 'right', vertical: 'middle' };
        row++;
      }
      if (serviceTotals.noGravados > 0) {
        sheet.getCell(row, 1).value = 'No grav.:';
        sheet.getCell(row, 1).font = { bold: true, color: { argb: teal } };
        sheet.getCell(row, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 1).border = borderThin;
        sheet.getCell(row, 2).value = Number(serviceTotals.noGravados);
        sheet.getCell(row, 2).numFmt = '"S/ "#,##0.00';
        sheet.getCell(row, 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
        sheet.getCell(row, 2).border = borderThin;
        sheet.getCell(row, 2).alignment = { horizontal: 'right', vertical: 'middle' };
        row++;
      }
      sheet.getCell(row, 1).value = 'Son:';
      sheet.getCell(row, 1).font = { bold: true, color: { argb: teal } };
      sheet.mergeCells(row, 2, row, 6);
      sheet.getCell(row, 2).value = totalInWords;
      sheet.getCell(row, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
      sheet.getCell(row, 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tealLight } };
      sheet.getCell(row, 1).border = borderThin;
      sheet.getCell(row, 2).border = borderThin;
      sheet.getCell(row, 2).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      sheet.getCell(row, 2).font = { italic: true };

      sheet.getColumn(1).width = 14;
      sheet.getColumn(2).width = 22;
      sheet.getColumn(3).width = 18;
      sheet.getColumn(4).width = 12;
      sheet.getColumn(5).width = 12;
      sheet.getColumn(6).width = 14;
      sheet.getColumn(7).width = 12;
      sheet.getColumn(8).width = 12;
      sheet.getColumn(9).width = 12;

      const buffer = await workbook.xlsx.writeBuffer();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      link.download = `Servicio_Continuo_${service.code || id}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      alert('Error al exportar a Excel. Intente de nuevo.');
    }
  };

  return (
    <>
      <style>{`
        @page {
          size: A4 ${useLandscape ? 'landscape' : 'portrait'};
          margin: 5mm;
        }
        
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          html {
            height: 100% !important;
            max-height: 100% !important;
            overflow: hidden !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: 100% !important;
            max-height: 100% !important;
            overflow: hidden !important;
            min-height: 0 !important;
          }
          .no-print {
            display: none !important;
          }
          .items-region-for-screen {
            display: none !important;
          }
          .items-region-for-print {
            display: block !important;
            margin-top: 0;
            margin-bottom: 0;
            padding-top: 0;
            padding-bottom: 0;
          }
          .items-region-for-print > .items-card:first-child {
            margin-top: 0;
          }
          .items-region-for-print > .items-card:last-child {
            page-break-after: avoid !important;
          }
          .print-container {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            margin: 0 auto !important;
            padding: 2mm !important;
            max-width: 100% !important;
            box-shadow: none !important;
            width: ${useLandscape ? '297mm' : '210mm'} !important;
            min-height: 0 !important;
            height: ${useLandscape ? '200mm' : '287mm'} !important;
            max-height: ${useLandscape ? '200mm' : '287mm'} !important;
            overflow: hidden !important;
            display: flex !important;
            flex-direction: column !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
          .print-content-inner {
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;
            overflow: hidden;
            padding-bottom: 24mm;
          }
          .print-body {
            flex: 1 1 auto;
            min-height: 0;
            overflow: hidden;
            position: relative;
          }
          .print-scaled-body {
            position: absolute;
            top: 0;
            left: 0;
            transform-origin: top left;
          }
          .header {
            margin-bottom: 0;
            padding-bottom: 0;
            border-bottom-width: 1px;
          }
          .header h1 {
            font-size: 6pt;
            margin: 0;
          }
          .info-compact {
            margin-bottom: 0;
            padding-bottom: 0;
            font-size: 7pt;
            line-height: 1.2;
            gap: 7px;
          }
          .info-compact-line {
            margin-bottom: 0;
            min-width: 120px;
          }
          .info-compact .status-badge {
            font-size: 7pt;
          }
          .cards-grid {
            gap: 4px;
            margin-bottom: 4px;
          }
          .card {
            padding: 3px 5px;
          }
          .card-title {
            font-size: 6pt;
            margin-bottom: 2px;
            padding-bottom: 2px;
            border-bottom-width: 1px;
          }
          .field {
            margin-bottom: 1px;
          }
          .field-label {
            font-size: 5pt;
          }
          .field-value {
            font-size: 6pt;
          }
          .status-badge {
            padding: 1px 4px;
            font-size: 5pt;
          }
          .items-card {
            margin-top: 0;
            padding: 1px 3px;
            width: 100%;
          }
          .items-region-for-print .items-card + .items-card {
            margin-top: 1px;
          }
          .items-title {
            font-size: 4.5pt;
            margin-bottom: 0;
            margin-top: 0;
            padding-bottom: 0;
            padding-top: 0;
          }
          .items-table {
            font-size: 3pt;
          }
          .items-table:not(.items-table-pivot) {
            font-size: 6pt;
          }
          .items-table:not(.items-table-pivot) th,
          .items-table:not(.items-table-pivot) td {
            font-size: 6pt;
            padding: 2px 3px;
            line-height: 1.1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .items-table:not(.items-table-pivot) .product-description-wrapper {
            display: inline;
            white-space: nowrap;
          }
          .items-table:not(.items-table-pivot) .product-description-wrapper .product-name,
          .items-table:not(.items-table-pivot) .product-description-wrapper .product-code {
            display: inline;
          }
          .items-table:not(.items-table-pivot) .product-description-wrapper .product-code::before {
            content: ' ';
          }
          .items-table:not(.items-table-pivot) th:nth-child(1),
          .items-table:not(.items-table-pivot) td:nth-child(1) { width: 9%; max-width: 9%; }
          .items-table:not(.items-table-pivot) th:nth-child(2),
          .items-table:not(.items-table-pivot) td:nth-child(2) { width: 35%; max-width: 35%; }
          .items-table:not(.items-table-pivot) th:nth-child(3),
          .items-table:not(.items-table-pivot) td:nth-child(3) { width: 22%; max-width: 22%; }
          .items-table:not(.items-table-pivot) th:nth-child(4),
          .items-table:not(.items-table-pivot) td:nth-child(4) { width: 8%; max-width: 8%; }
          .items-table:not(.items-table-pivot) th:nth-child(5),
          .items-table:not(.items-table-pivot) td:nth-child(5) { width: 12%; max-width: 12%; }
          .items-table:not(.items-table-pivot) th:nth-child(6),
          .items-table:not(.items-table-pivot) td:nth-child(6) { width: 14%; max-width: 14%; }
          .items-table-pivot th,
          .items-table-pivot td {
            padding: 0 1px;
            line-height: 0.85;
          }
          .items-table-pivot thead th {
            font-size: 2.5pt;
            line-height: 0.85;
          }
          .items-table-pivot {
            font-size: 3pt;
          }
          .items-table th,
          .items-table td {
            padding: 0 2px;
            line-height: 0.95;
          }
          .items-table th {
            font-size: 3pt;
          }
          .items-table:not(.items-table-pivot) th {
            font-size: 6pt;
          }
          .items-table-pivot tr,
          .items-table-pivot tr {
            line-height: 1;
          }
          .pivot-service-card {
            margin-bottom: 1px;
          }
          .totals-section {
            margin-top: 2px;
          }
          .totals-card {
            padding: 2px 4px;
            width: 180px;
          }
          .total-row {
            margin-bottom: 0;
            font-size: 4.5pt;
          }
          .total-final {
            margin-top: 1px;
            padding-top: 1px;
            font-size: 5pt;
          }
          .total-final .total-value {
            font-size: 6pt;
          }
          .total-in-words {
            margin-top: 1px;
            padding-top: 1px;
            font-size: 3.5pt;
          }
          .notes-section {
            margin-top: 2px;
            padding: 2px 3px;
          }
          .notes-title {
            font-size: 4.5pt;
            margin-bottom: 0;
          }
          .notes-content {
            font-size: 3.5pt;
          }
          .print-footer-compact {
            margin-top: 0;
            padding-top: 0;
            padding-bottom: 0;
            gap: 0 6px;
            font-size: 6pt;
            display: grid;
            grid-template-columns: minmax(120px, 1fr) auto;
            grid-template-rows: 1fr;
            align-items: stretch;
          }
          .print-footer-obs {
            border: 1px solid #0d9488;
            border-radius: 2px;
            padding: 2px 4px;
            background: #f0fdfa;
            min-width: 0;
            min-height: 20px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            gap: 0;
          }
          .print-obs-label {
            flex-shrink: 0;
            font-weight: 600;
            color: #0d9488;
          }
          .print-obs-text {
            white-space: normal;
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: normal;
            width: 100%;
            line-height: 1.15;
            color: #374151;
          }
          .print-footer-totals {
            border: 1px solid #0d9488;
            border-radius: 2px;
            padding: 2px 4px;
            background: #f0fdfa;
            gap: 0 4px;
            min-height: 20px;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            align-content: center;
          }
          .print-footer-obs,
          .print-obs-label,
          .print-footer-totals,
          .print-total-line,
          .print-total-final,
          .print-total-words {
            font-size: 6pt;
          }
          .print-total-label {
            font-weight: 600;
            color: #0d9488 !important;
          }
          .print-total-num {
            color: #6b7280 !important;
          }
          .print-total-final .print-total-label {
            color: #0d9488 !important;
          }
          .print-total-final .print-total-num {
            color: #6b7280 !important;
          }
          .print-bottom-row {
            margin-top: 2px;
            gap: 4px;
          }
          .print-footer-compact {
            page-break-before: avoid;
          }
          .items-card,
          .print-footer-compact,
          .print-bottom-row,
          .notes-section,
          .print-firmas {
            page-break-inside: avoid;
          }
          .print-firmas {
            position: fixed !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            width: ${useLandscape ? '297mm' : '210mm'} !important;
            max-width: 100% !important;
            flex: none !important;
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            padding: 3mm 2mm 2mm !important;
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 12px !important;
            align-items: end !important;
            border-top: 1px solid #e5e7eb !important;
            background: white !important;
            box-sizing: border-box !important;
          }
          .print-firma-block {
            text-align: center;
            padding-top: 4px;
          }
          .print-firma-empresa {
            font-size: 4pt;
            font-weight: 700;
            color: #0d9488;
            text-transform: uppercase;
            margin-bottom: 2px;
            word-break: break-word;
          }
          .print-firma-linea {
            border-bottom: 1px dotted #374151;
            height: 5px;
            margin-bottom: 1px;
          }
          .print-firma-nombre {
            font-size: 3.5pt;
            color: #6b7280;
          }
        }
        
        @media screen {
          body {
            background: #f3f4f6;
          }
          .items-region-for-screen {
            display: none !important;
          }
          .items-region-for-print {
            display: block !important;
          }
          .print-container {
            max-width: ${useLandscape ? '297mm' : '210mm'};
            margin: 20px auto;
            background: white;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            min-height: ${useLandscape ? '210mm' : '297mm'};
          }
          .print-actions {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 1000;
            display: flex;
            gap: 10px;
          }
        }
        
        * {
          box-sizing: border-box;
        }
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
          font-size: 11pt;
          color: #1f2937;
          line-height: 1.6;
          background: white;
        }
        
        .print-container {
          background: white;
          padding: 10mm 8mm;
          width: ${useLandscape ? '297mm' : '210mm'};
          display: flex;
          flex-direction: column;
          min-height: ${useLandscape ? '210mm' : '297mm'};
        }
        .print-content-inner {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0;
        }
        .print-body {
          flex: 1 1 auto;
          min-height: 0;
          overflow: hidden;
          position: relative;
        }
        .print-scaled-body {
          position: absolute;
          top: 0;
          left: 0;
          transform-origin: top left;
        }
        .print-firmas {
          flex: 0 0 auto;
          margin-top: auto;
        }
        
        .header {
          text-align: center;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 2px solid #0d9488;
        }
        
        .header h1 {
          font-size: 18pt;
          font-weight: bold;
          color: #0d9488;
          margin: 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .info-compact {
          margin-bottom: 12px;
          font-size: 10pt;
          line-height: 1.5;
          color: #374151;
          display: flex;
          flex-direction: row;
          gap: 16px;
          align-items: flex-start;
          flex-wrap: wrap;
        }
        .info-compact-line {
          margin-bottom: 0;
          flex: 1;
          min-width: 200px;
        }
        .info-compact-line:last-child {
          margin-bottom: 0;
        }
        .info-compact-label {
          font-weight: 600;
          color: #0d9488;
        }
        .info-compact-sep {
          color: #9ca3af;
        }
        
        .cards-grid {
          margin-bottom: 12px;
          font-size: 10pt;
          line-height: 1.5;
          color: #374151;
        }
        .cards-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
        }
        
        .card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 12px;
        }
        
        .card-title {
          font-weight: 700;
          font-size: 12pt;
          color: #0d9488;
          margin-bottom: 10px;
          padding-bottom: 6px;
          border-bottom: 2px solid #0d9488;
          text-transform: uppercase;
        }
        
        .field {
          margin-bottom: 8px;
        }
        
        .field:last-child {
          margin-bottom: 0;
        }
        
        .field-label {
          font-size: 9pt;
          color: #6b7280;
          font-weight: 600;
          margin-bottom: 2px;
          text-transform: uppercase;
        }
        
        .field-value {
          font-size: 10pt;
          color: #1f2937;
          font-weight: 500;
        }
        
        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 9pt;
          font-weight: 600;
          text-transform: uppercase;
        }
        
        .items-card {
          margin-top: 13px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 10px;
        }
        
        .items-title {
          font-weight: 700;
          font-size: 11pt;
          color: #0d9488;
          margin-bottom: 11px;
          padding-bottom: 5px;
          border-bottom: 2px solid #0d9488;
          text-transform: uppercase;
        }
        
        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 8pt;
          table-layout: fixed;
        }
        
        .items-table-pivot {
          font-size: ${manyColumns ? '6pt' : '7pt'};
          table-layout: fixed;
          width: 100%;
        }
        
        .items-table-pivot th,
        .items-table-pivot td {
          padding: ${manyColumns ? '2px 4px' : '4px 5px'};
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 0;
          min-width: 0;
          line-height: 0.85;
        }
        
        .items-table-pivot th:first-child,
        .items-table-pivot td:first-child {
          max-width: none;
          min-width: 63px;
          width: 63px;
        }
        
        .items-table-pivot th:last-child,
        .items-table-pivot td:last-child {
          width: 47px;
          min-width: 47px;
          max-width: none;
        }
        
        .items-table-pivot thead th {
          font-size: ${manyColumns ? '5pt' : '6pt'};
          line-height: 0.85;
          white-space: normal;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        .items-table thead {
          background: #0d9488;
          color: white;
        }
        
        .items-table th {
          padding: 7px 5px;
          text-align: left;
          font-weight: 700;
          text-transform: uppercase;
          font-size: 7pt;
          border: 1px solid #0d9488;
        }
        
        .items-table:not(.items-table-pivot) th {
          padding: 7px 5px;
        }
        .items-table:not(.items-table-pivot) td {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .items-table:not(.items-table-pivot) .product-description-wrapper {
          display: inline;
          white-space: nowrap;
        }
        .items-table:not(.items-table-pivot) .product-description-wrapper .product-name,
        .items-table:not(.items-table-pivot) .product-description-wrapper .product-code {
          display: inline;
        }
        .items-table:not(.items-table-pivot) .product-description-wrapper .product-code::before {
          content: ' ';
        }
        .items-table:not(.items-table-pivot) th:nth-child(1),
        .items-table:not(.items-table-pivot) td:nth-child(1) { width: 9%; max-width: 9%; }
        .items-table:not(.items-table-pivot) th:nth-child(2),
        .items-table:not(.items-table-pivot) td:nth-child(2) { width: 35%; max-width: 35%; }
        .items-table:not(.items-table-pivot) th:nth-child(3),
        .items-table:not(.items-table-pivot) td:nth-child(3) { width: 22%; max-width: 22%; }
        .items-table:not(.items-table-pivot) th:nth-child(4),
        .items-table:not(.items-table-pivot) td:nth-child(4) { width: 8%; max-width: 8%; }
        .items-table:not(.items-table-pivot) th:nth-child(5),
        .items-table:not(.items-table-pivot) td:nth-child(5) { width: 12%; max-width: 12%; }
        .items-table:not(.items-table-pivot) th:nth-child(6),
        .items-table:not(.items-table-pivot) td:nth-child(6) { width: 14%; max-width: 14%; }
        .items-table td {
          padding: 7px 5px;
          border: 1px solid #e5e7eb;
          background: white;
        }
        
        .items-table tbody tr:nth-child(even) {
          background: #f9fafb;
        }
        
        .items-table-pivot td {
          text-align: right;
        }
        .items-table-pivot td:first-child {
          text-align: left;
        }
        .items-table-pivot .row-total {
          font-weight: 600;
        }
        .items-table-pivot tr.pivot-total-row td {
          background: #fef3c7;
          font-weight: 600;
        }
        .items-table-pivot td.grand-total {
          background: #fde68a;
          font-weight: 700;
        }
        .pivot-service-card {
          margin-bottom: 16px;
        }
        .pivot-table-wrapper {
          width: 100%;
          overflow: hidden;
        }
        @media screen {
          .pivot-table-wrapper {
            overflow: hidden;
            max-width: 100%;
          }
        }
        @media print {
          .pivot-table-wrapper {
            overflow: visible;
          }
        }
        .pivot-service-card:last-child {
          margin-bottom: 0;
        }
        
        .product-description-wrapper {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        
        .product-name {
          font-weight: 600;
          color: #1f2937;
        }
        
        .product-code {
          font-size: 8pt;
          color: #6b7280;
        }
        
        .print-bottom-row {
          display: flex;
          gap: 12px;
          margin-top: 12px;
          align-items: flex-start;
          width: 100%;
        }
        .print-footer-compact {
          display: grid;
          grid-template-columns: minmax(200px, 1fr) auto;
          grid-template-rows: 1fr;
          gap: 0 16px;
          margin-top: 12px;
          width: 100%;
          align-items: stretch;
          font-size: 9pt;
        }
        .print-footer-obs {
          min-width: 0;
          border: 1px solid #0d9488;
          border-radius: 6px;
          padding: 10px 12px;
          background: #f0fdfa;
          min-height: 48px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          gap: 4px 0;
        }
        .print-obs-label {
          flex-shrink: 0;
          font-weight: 600;
          color: #0d9488;
        }
        .print-obs-text {
          white-space: normal;
          word-wrap: break-word;
          overflow-wrap: break-word;
          word-break: normal;
          width: 100%;
          line-height: 1.3;
          color: #374151;
        }
        .print-footer-totals {
          display: flex;
          flex-wrap: wrap;
          gap: 4px 12px;
          align-items: center;
          align-content: center;
          border: 1px solid #0d9488;
          border-radius: 6px;
          padding: 10px 12px;
          background: #f0fdfa;
          min-height: 48px;
        }
        .print-total-line {
          color: #374151;
        }
        .print-total-label {
          font-weight: 600;
          color: #0d9488;
        }
        .print-total-num {
          color: #6b7280;
          font-weight: normal;
        }
        .print-total-final {
          font-weight: 700;
          color: #0d9488;
        }
        .print-total-final .print-total-label {
          color: #0d9488;
        }
        .print-total-final .print-total-num {
          color: #6b7280;
        }
        .print-total-words {
          color: #374151;
        }
        .print-total-words .print-total-label {
          font-weight: 600;
          color: #0d9488;
        }
        .print-total-words .print-total-num {
          color: #6b7280;
          font-style: italic;
        }
        .print-bottom-left {
          flex: 1;
          min-width: 0;
        }
        .print-bottom-row .totals-section {
          flex-shrink: 0;
          margin-top: 0;
        }
        
        .totals-section {
          margin-top: 15px;
          display: flex;
          justify-content: flex-end;
        }
        
        .totals-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 12px;
          width: 300px;
        }
        
        .total-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;
          font-size: 10pt;
        }
        
        .total-row:last-child {
          margin-bottom: 0;
        }
        
        .total-label {
          color: #6b7280;
          font-weight: 600;
        }
        
        .total-value {
          color: #1f2937;
          font-weight: 600;
        }
        
        .total-final {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 2px solid #0d9488;
          font-size: 12pt;
        }
        
        .total-final .total-label {
          color: #1f2937;
          font-weight: 700;
        }
        
        .total-final .total-value {
          color: #0d9488;
          font-weight: 700;
          font-size: 14pt;
        }
        
        .total-in-words {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #e5e7eb;
          font-size: 9pt;
          color: #6b7280;
          font-style: italic;
        }
        
        .notes-section {
          margin-top: 15px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 12px;
        }
        
        .notes-title {
          font-weight: 700;
          font-size: 12pt;
          color: #0d9488;
          margin-bottom: 8px;
          text-transform: uppercase;
        }
        
        .notes-content {
          font-size: 10pt;
          color: #1f2937;
          white-space: pre-wrap;
        }
        .print-firmas {
          margin-top: 32px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          align-items: end;
        }
        .print-firma-block {
          text-align: center;
          padding-top: 40px;
        }
        .print-firma-empresa {
          font-size: 10pt;
          font-weight: 700;
          color: #0d9488;
          text-transform: uppercase;
          margin-bottom: 8px;
          word-break: break-word;
        }
        .print-firma-linea {
          border-bottom: 1px dotted #374151;
          height: 24px;
          margin-bottom: 6px;
        }
        .print-firma-nombre {
          font-size: 9pt;
          color: #6b7280;
        }
      `}</style>

      <div className="no-print print-actions">
        <button
          onClick={() => window.print()}
          className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium shadow-md transition-colors"
        >
          🖨️ Imprimir
        </button>
        <button
          onClick={exportToExcel}
          className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium shadow-md transition-colors"
        >
          📊 Exportar a Excel
        </button>
        <button
          onClick={() => window.close()}
          className="px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium shadow-md transition-colors"
        >
          ✕ Cerrar
        </button>
      </div>

      <div className="print-container">
        <div className="print-content-inner">
          <div className="print-body">
            <div
              ref={printContentRef}
              className="print-scaled-body"
              style={{
                transform: `scale(${printScale}, ${printScale * 0.72})`,
                transformOrigin: 'top left'
              }}
            >
        {/* Header */}
        <div className="header">
          <h1>Servicio Continuo</h1>
        </div>

        {/* Información básica y proveedor: diseño simple en pocas líneas */}
        <div className="info-compact">
          <div className="info-compact-line">
            <span className="info-compact-label">Servicio:</span>{' '}
            {service.code}
            <span className="info-compact-sep"> · </span>
            <span className="info-compact-label">Inicio:</span> {startDate}
            <span className="info-compact-sep"> · </span>
            <span className="info-compact-label">Fin:</span> {endDate}
            <span className="info-compact-sep"> · </span>
            <span className="info-compact-label">Estado:</span>{' '}
            <span className="status-badge" style={{ backgroundColor: statusColor + '20', color: statusColor }}>{statusLabel}</span>
          </div>
          <div className="info-compact-line">
            <span className="info-compact-label">Proveedor:</span>{' '}
            {service.supplier?.name || 'N/A'}
            {service.supplier?.document_number && (
              <>
                <span className="info-compact-sep"> · </span>
                <span className="info-compact-label">
                  {service.supplier.document_type === 'ruc' ? 'RUC' : service.supplier.document_type === 'dni' ? 'DNI' : 'Código'}:
                </span>{' '}
                {service.supplier.document_number}
              </>
            )}
            {includeAreas && service.supplier?.address && (
              <>
                <span className="info-compact-sep"> · </span>
                <span className="info-compact-label">Dir.:</span> {service.supplier.address}
              </>
            )}
          </div>
        </div>
        {/* Vista previa: tablas completas (sin partición) */}
        <div className="items-region-for-screen">
          {pivotsByService.length > 0 ? (
            <>
              {pivotsByService.map((pivot) => (
                <div key={pivot.serviceKey} className="items-card pivot-service-card">
                  <div className="items-title">
                    Registros de Avance {pivot.serviceSku ? `– ${pivot.serviceName} (${pivot.serviceSku})` : `– ${pivot.serviceName}`}
                  </div>
                  <div className="pivot-table-wrapper">
                    <table className="items-table items-table-pivot">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          {pivot.areasList.map((a) => (
                            <th key={a.key}>{a.name || a.code || '-'}</th>
                          ))}
                          <th>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pivot.dates.map((d) => {
                          let rowTotal = 0;
                          return (
                            <tr key={d}>
                              <td>{formatDateLocaleLima(d)}</td>
                              {pivot.areasList.map((a) => {
                                const val = (pivot.grid[d] && pivot.grid[d][a.key]) || 0;
                                rowTotal += val;
                                return (
                                  <td key={a.key}>{val === 0 ? '' : parseFloat(val).toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}</td>
                                );
                              })}
                              <td className="row-total">{rowTotal.toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}</td>
                            </tr>
                          );
                        })}
                        <tr className="pivot-total-row">
                          <td><strong>TOTAL</strong></td>
                          {pivot.areasList.map((a) => (
                            <td key={a.key}>{pivot.columnTotals[a.key] === 0 ? '' : pivot.columnTotals[a.key].toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}</td>
                          ))}
                          <td className="grand-total">{pivot.grandTotal.toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </>
          ) : sortedProgressByProduct.length > 0 ? (
            <div className="items-card">
              <div className="items-title">Registros de Avance</div>
              {groupedByService.map((g, gIdx) => (
                <div key={gIdx} className="mb-4">
                  <div className="text-sm font-semibold text-teal-700 mb-1.5">
                    {g.service ? (
                      <>
                        {g.service.name}
                        {g.service.sku && <span className="text-gray-600 font-normal"> ({g.service.sku})</span>}
                      </>
                    ) : (
                      'Sin servicio'
                    )}
                  </div>
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Proyecto</th>
                        <th>Descripción</th>
                        <th>Cant.</th>
                        <th>P.Unit</th>
                        <th>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map((item, index) => (
                        <tr key={index}>
                          <td>{formatDateLocaleLima(item.progress_date)}</td>
                          <td>
                            {item.project ? (
                              <div className="product-description-wrapper">
                                <span className="product-name">{item.project.code}</span>
                                <span className="product-code">{item.project.name}</span>
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>{item.description || '-'}</td>
                          <td>{parseFloat(item.quantity).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                          <td>S/ {parseFloat(item.unit_price).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                          <td>S/ {parseFloat(item.subtotal).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Impresión: una sola hoja por servicio (tabla pivote completa o detalle completo) */}
        <div className="items-region-for-print">
        {pivotsByService.length > 0 ? (
          <>
            {pivotsByService.map((pivot) => (
                <div
                  key={pivot.serviceKey}
                  className="items-card pivot-service-card"
                >
                  <div className="items-title">
                    Registros de Avance {pivot.serviceSku ? `– ${pivot.serviceName} (${pivot.serviceSku})` : `– ${pivot.serviceName}`}
                  </div>
                  <div className="pivot-table-wrapper">
                    <table className="items-table items-table-pivot">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          {pivot.areasList.map((a) => (
                            <th key={a.key}>{a.name || a.code || '-'}</th>
                          ))}
                          <th>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pivot.dates.map((d) => {
                          let rowTotal = 0;
                          return (
                            <tr key={d}>
                              <td>{formatDateLocaleLima(d)}</td>
                              {pivot.areasList.map((a) => {
                                const val = (pivot.grid[d] && pivot.grid[d][a.key]) || 0;
                                rowTotal += val;
                                return (
                                  <td key={a.key}>{val === 0 ? '' : parseFloat(val).toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}</td>
                                );
                              })}
                              <td className="row-total">{rowTotal.toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}</td>
                            </tr>
                          );
                        })}
                        <tr className="pivot-total-row">
                          <td><strong>TOTAL</strong></td>
                          {pivot.areasList.map((a) => (
                            <td key={a.key}>{pivot.columnTotals[a.key] === 0 ? '' : pivot.columnTotals[a.key].toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}</td>
                          ))}
                          <td className="grand-total">{pivot.grandTotal.toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
            ))}
          </>
        ) : detailBlocksForPrint.length > 0 ? (
          detailBlocksForPrint.map((block, blockIndex) => (
            <div
              key={blockIndex}
              className="items-card"
            >
              <div className="items-title">Registros de Avance</div>
              <div className="text-sm font-semibold text-teal-700 mb-1.5">
                {block.serviceSku
                  ? `${block.serviceName} (${block.serviceSku})${block.isContinuation ? ' — continuación' : ''}`
                  : `${block.serviceName}${block.isContinuation ? ' — continuación' : ''}`}
              </div>
              <table className="items-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Proyecto</th>
                    <th>Descripción</th>
                    <th>Cant.</th>
                    <th>P.Unit</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {block.items.map((item, index) => (
                    <tr key={index}>
                      <td>{formatDateLocaleLima(item.progress_date)}</td>
                      <td>
                        {item.project ? (
                          <div className="product-description-wrapper">
                            <span className="product-name">{item.project.code}</span>
                            <span className="product-code">{item.project.name}</span>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{item.description || '-'}</td>
                      <td>{parseFloat(item.quantity).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                      <td>S/ {parseFloat(item.unit_price).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                      <td>S/ {parseFloat(item.subtotal).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        ) : null}
        </div>

        {/* Observaciones y montos en una sola línea compacta */}
        <div className="print-footer-compact">
          <div className="print-footer-obs">
            {service.notes ? (
              <span className="print-obs-label">Observaciones:</span>
            ) : null}
            {service.notes ? (
              <span className="print-obs-text">{service.notes}</span>
            ) : null}
          </div>
          <div className="print-footer-totals">
            {serviceTotals.baseImponible18 > 0 && (
              <span className="print-total-line"><span className="print-total-label">Base 18%:</span> <span className="print-total-num">S/ {formatCurrency(serviceTotals.baseImponible18)}</span> · <span className="print-total-label">IGV:</span> <span className="print-total-num">S/ {formatCurrency(serviceTotals.igv18)}</span></span>
            )}
            {serviceTotals.baseImponible10 > 0 && (
              <span className="print-total-line"> <span className="print-total-label">Base 10%:</span> <span className="print-total-num">S/ {formatCurrency(serviceTotals.baseImponible10)}</span> · <span className="print-total-label">IGV:</span> <span className="print-total-num">S/ {formatCurrency(serviceTotals.igv10)}</span></span>
            )}
            {serviceTotals.baseImponible10_5 > 0 && (
              <span className="print-total-line"> <span className="print-total-label">Base 10.5%:</span> <span className="print-total-num">S/ {formatCurrency(serviceTotals.baseImponible10_5)}</span> · <span className="print-total-label">IGV:</span> <span className="print-total-num">S/ {formatCurrency(serviceTotals.igv10_5)}</span></span>
            )}
            {serviceTotals.noGravados > 0 && (
              <span className="print-total-line"> <span className="print-total-label">No grav.:</span> <span className="print-total-num">S/ {formatCurrency(serviceTotals.noGravados)}</span></span>
            )}
            <span className="print-total-final"><span className="print-total-label">Total:</span> <span className="print-total-num">S/ {formatCurrency(serviceTotals.total)}</span></span>
            <span className="print-total-words"><span className="print-total-label">Son:</span> <span className="print-total-num">{totalInWords}</span></span>
          </div>
        </div>

            </div>
          </div>

        {/* Pie de página: firmas siempre al final de la hoja */}
        <div className="print-firmas">
          <div className="print-firma-block">
            <div className="print-firma-linea" />
            <div className="print-firma-empresa">CONTRATANTE</div>
          </div>
          <div className="print-firma-block">
            <div className="print-firma-linea" />
            <div className="print-firma-empresa">{service.supplier?.name || 'PROVEEDOR'}</div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default PrintContinuousService;
